import { useEffect, useState } from "react";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUp, ArrowDown } from "lucide-react";
import { usePlayer } from "../contexts/PlayerContext";

const MAX_KEY = "mboa_nav_max";
const idxNow = () => (window.history.state && typeof window.history.state.idx === "number" ? window.history.state.idx : 0);

/** Flèches de navigation flottantes : page précédente / suivante, remonter / descendre.
 *  Chaque bouton n'apparaît que s'il sert (pas de « retour » sur la première page visitée, etc.). */
export default function NavArrows() {
    const nav = useNavigate();
    const location = useLocation();
    const navTypeNow = useNavigationType();   // PUSH = nouvelle page, POP = retour/avant, REPLACE
    const { current, expanded } = usePlayer() || {};
    const [idx, setIdx] = useState(0);
    const [maxIdx, setMaxIdx] = useState(0);
    const [scroll, setScroll] = useState({ top: true, bottom: true });

    // Position dans l'historique ; la plus haute atteinte dit si « page suivante » existe.
    // Ouvrir une nouvelle page efface les pages « suivantes », comme dans un navigateur.
    useEffect(() => {
        const i = idxNow();
        let m = 0;
        try { m = parseInt(sessionStorage.getItem(MAX_KEY) || "0", 10) || 0; } catch { /* stockage indisponible */ }
        m = navTypeNow === "PUSH" ? i : Math.max(m, i);
        try { sessionStorage.setItem(MAX_KEY, String(m)); } catch { /* stockage indisponible */ }
        setIdx(i);
        setMaxIdx(m);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.key]);

    useEffect(() => {
        const onScroll = () => {
            const y = window.scrollY;
            const max = document.documentElement.scrollHeight - window.innerHeight;
            setScroll({ top: y < 300, bottom: max - y < 300 });
        };
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", onScroll);
        const t = setTimeout(onScroll, 800);   // la page a fini de charger son contenu
        return () => { window.removeEventListener("scroll", onScroll); window.removeEventListener("resize", onScroll); clearTimeout(t); };
    }, [location.pathname]);

    if (expanded) return null;
    const canBack = idx > 0;
    const canFwd = idx < maxIdx;
    const btns = [
        canBack && { key: "back", icon: <ArrowLeft className="w-4 h-4" />, label: "Page précédente", on: () => nav(-1) },
        canFwd && { key: "fwd", icon: <ArrowRight className="w-4 h-4" />, label: "Page suivante", on: () => nav(1) },
        !scroll.top && { key: "up", icon: <ArrowUp className="w-4 h-4" />, label: "Remonter en haut", on: () => window.scrollTo({ top: 0, behavior: "smooth" }) },
        !scroll.bottom && { key: "down", icon: <ArrowDown className="w-4 h-4" />, label: "Descendre en bas", on: () => window.scrollTo({ top: document.documentElement.scrollHeight, behavior: "smooth" }) },
    ].filter(Boolean);
    if (!btns.length) return null;

    return (
        <div
            className="fixed right-3 md:right-5 z-30 flex items-center gap-1 rounded-full border border-[#262626] bg-[#0d0d0d]/85 backdrop-blur px-1 py-1 shadow-lg transition-[bottom]"
            style={{ bottom: current ? 104 : 20 }}
            data-testid="nav-arrows"
        >
            {btns.map((b) => (
                <button key={b.key} onClick={b.on} aria-label={b.label} title={b.label}
                    className="w-9 h-9 rounded-full text-[#C8C8C8] hover:text-[#0F0F0F] hover:bg-[#00d97e] flex items-center justify-center transition-colors"
                    data-testid={`nav-${b.key}`}>
                    {b.icon}
                </button>
            ))}
        </div>
    );
}
